import type { CartItem } from '../Service/CartStorageService'

import CartService, { CART_CHANGE_EVENT, type CartChangeDetail } from '../Service/CartService'
import ToastService from '../Service/ToastService'
import { getElement, getElementChild } from '../Utility/Dom'
import { applyPriceTooltipVariant, formatPrice, resolveCartPrice } from '../Utility/Price'
import { IS_HIDDEN } from '../Utility/State'
import CartCountdown from './CartCountdown'

const dateFormatter = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })

const ONLINE_FORMATS = new Set(['e_learning', 'webinar'])

const formatDate = (value: null | string | undefined): string | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  return dateFormatter.format(date)
}

// Multi-day modules arrive without a single date id (isDateRange); render the aggregated
// span so they read differently from a single-date booking.
const formatDateRange = (start: null | string | undefined, end: null | string | undefined): string | undefined => {
  const from = formatDate(start)
  if (from === undefined) return undefined
  const to = formatDate(end)
  return to === undefined || to === from ? from : `${from} – ${to}`
}

const setText = (element: HTMLElement | null, value: null | string | undefined): void => {
  if (element === null) return
  if (value === undefined || value === null || value === '') {
    element.classList.add(IS_HIDDEN)
    return
  }
  element.textContent = value
  element.classList.remove(IS_HIDDEN)
}

const stepInputValue = (input: HTMLInputElement | null, delta: number): void => {
  if (input === null) return
  const min = Number(input.min) || 1
  const current = Number(input.value) || min
  const next = Math.max(min, current + delta)
  if (next === current) return
  input.value = next.toString()
  input.dispatchEvent(new Event('change', { bubbles: true }))
}

class CartDialog {
  private readonly boundHandleBackdropClick = this.handleBackdropClick.bind(this)
  private readonly boundHandleChange = this.handleChange.bind(this)
  private readonly boundHandleClose = this.handleClose.bind(this)
  private readonly boundHandleExtend = this.handleExtend.bind(this)
  private readonly boundHandleKeyDown = this.handleKeyDown.bind(this)
  private readonly boundHandleSubmit = this.handleSubmit.bind(this)
  private readonly closeButton: HTMLElement | null
  private readonly count: HTMLElement | null
  private countdown: CartCountdown | null = null
  private readonly dialog: HTMLDialogElement | null
  private readonly emptyState: HTMLElement | null
  private readonly extendButton: HTMLElement | null
  private readonly form: HTMLFormElement | null
  private readonly itemsContainer: HTMLElement | null
  private readonly maxParticipants: number
  private readonly moduleChildTemplate: HTMLTemplateElement | null
  private readonly moduleCountTemplate: string
  private readonly modulePositionTemplate: string
  private readonly moduleTemplate: HTMLTemplateElement | null
  private readonly moduleUnitLabel: string
  private readonly notice: HTMLElement | null
  private readonly noticeCountdown: HTMLElement | null
  private readonly onlineLocationLabel: string
  private readonly simpleTemplate: HTMLTemplateElement | null
  private readonly triggerButton: HTMLElement | null

  constructor() {
    this.dialog = getElement('cart-dialog') as HTMLDialogElement | null
    this.closeButton = getElement('cart-dialog-close')
    this.count = getElement('cart-dialog-count')
    this.itemsContainer = getElement('cart-dialog-items')
    this.emptyState = getElement('cart-dialog-empty')
    this.notice = getElement('cart-dialog-notice')
    this.noticeCountdown = getElement('cart-dialog-notice-countdown')
    this.extendButton = getElement('cart-dialog-extend')
    this.form = getElement('cart-dialog-form') as HTMLFormElement | null
    this.simpleTemplate = getElement('cart-item-template') as HTMLTemplateElement | null
    this.moduleTemplate = getElement('cart-item-module-template') as HTMLTemplateElement | null
    this.moduleChildTemplate = getElement('cart-item-module-child-template') as HTMLTemplateElement | null
    this.triggerButton = getElement('cart-button')
    this.maxParticipants = Number(this.dialog?.dataset.cartMaxParticipants) || Infinity
    this.onlineLocationLabel = this.dialog?.dataset.cartLocationOnline ?? ''
    this.moduleCountTemplate = this.dialog?.dataset.cartModuleCount ?? ''
    this.moduleUnitLabel = this.dialog?.dataset.cartModuleUnit ?? ''
    this.modulePositionTemplate = this.dialog?.dataset.cartModulePosition ?? ''
  }

  public init(): void {
    if (this.dialog === null) return

    if (this.notice !== null && this.noticeCountdown !== null) {
      this.countdown = new CartCountdown(this.notice, this.noticeCountdown)
    }

    this.closeButton?.addEventListener('click', this.boundHandleClose)
    this.dialog.addEventListener('click', this.boundHandleBackdropClick)
    this.dialog.addEventListener('keydown', this.boundHandleKeyDown)
    this.extendButton?.addEventListener('click', this.boundHandleExtend)
    this.form?.addEventListener('submit', this.boundHandleSubmit)
    document.addEventListener(CART_CHANGE_EVENT, this.boundHandleChange)

    const cart = CartService.getCart()
    this.render(cart)
  }

  private bindQuantityStepper(node: HTMLElement, item: CartItem, inputHook: string, labelHook: string, decrementHook: string, incrementHook: string): void {
    const input = getElementChild(node, inputHook) as HTMLInputElement | null
    const label = getElementChild(node, labelHook) as HTMLLabelElement | null
    if (input !== null) {
      input.value = item.quantity.toString()
      if (Number.isFinite(this.maxParticipants)) input.max = this.maxParticipants.toString()
    }
    if (input !== null && label !== null) {
      const inputId = `${inputHook}-${item.itemId}`
      input.id = inputId
      label.htmlFor = inputId
    }
    getElementChild(node, decrementHook)?.addEventListener('click', (): void => stepInputValue(input, -1))
    getElementChild(node, incrementHook)?.addEventListener('click', (): void => {
      if (input !== null && Number(input.value) >= this.maxParticipants) {
        ToastService.showFromElement('cart-toast-max-participants')
        return
      }
      stepInputValue(input, 1)
    })
    input?.addEventListener('change', (): void => {
      const quantity = Number(input.value)
      if (Number.isNaN(quantity) || quantity < 1) return
      // Revert to the server-backed value on rejection; a success re-renders the row.
      void CartService.updateItem(item.itemId, quantity).then((ok: boolean): void => {
        if (ok === false) input.value = item.quantity.toString()
      })
    })
  }

  private buildItem(item: CartItem): HTMLElement | null {
    const node = this.simpleTemplate?.content.firstElementChild?.cloneNode(true) as HTMLElement | undefined
    if (!node) return null

    node.dataset.itemId = item.itemId

    setText(getElementChild(node, 'cart-item-title'), item.course.title)
    setText(getElementChild(node, 'cart-item-date'), this.resolveDateText(item))
    setText(getElementChild(node, 'cart-item-location'), this.resolveLocationText(item))
    setText(getElementChild(node, 'cart-item-va-number'), item.course.vaNumber)
    setText(getElementChild(node, 'cart-item-price'), formatPrice(resolveCartPrice(item.course.price, item.course.memberPrice, item.isMemberPrice)))

    const memberPrice = Number(item.course.memberPrice) > 0 ? item.course.memberPrice : undefined
    setText(getElementChild(node, 'cart-item-member-price-amount'), memberPrice === undefined ? undefined : formatPrice(memberPrice))
    getElementChild(node, 'cart-item-member-price')?.classList.toggle(IS_HIDDEN, memberPrice === undefined)

    this.linkTooltip(node, item, 'cart-item-price-info', 'cart-item-price-info-tt')
    this.bindQuantityStepper(node, item, 'cart-item-participants', 'cart-item-participants-label', 'cart-item-decrement', 'cart-item-increment')
    getElementChild(node, 'cart-item-remove')?.addEventListener('click', (): void => {
      void CartService.removeItem(item.itemId)
    })

    return node
  }

  private buildModuleChild(item: CartItem): HTMLElement | null {
    const node = this.moduleChildTemplate?.content.firstElementChild?.cloneNode(true) as HTMLElement | undefined
    if (!node) return null

    node.dataset.itemId = item.itemId

    const position = item.series?.modulePosition
    setText(getElementChild(node, 'cart-item-child-chip'), position === null || position === undefined ? undefined : this.modulePositionTemplate.replace('{0}', position.toString()))
    setText(getElementChild(node, 'cart-item-child-title'), item.course.title)
    setText(getElementChild(node, 'cart-item-child-date'), this.resolveDateText(item))
    setText(getElementChild(node, 'cart-item-child-location'), this.resolveLocationText(item))
    setText(getElementChild(node, 'cart-item-child-va-number'), item.course.vaNumber)
    setText(getElementChild(node, 'cart-item-child-price'), formatPrice(resolveCartPrice(item.course.price, item.course.memberPrice, item.isMemberPrice)))

    const memberPrice = Number(item.course.memberPrice) > 0 ? item.course.memberPrice : undefined
    setText(getElementChild(node, 'cart-item-child-member-price-amount'), memberPrice === undefined ? undefined : formatPrice(memberPrice))
    getElementChild(node, 'cart-item-child-member-price')?.classList.toggle(IS_HIDDEN, memberPrice === undefined)

    this.linkTooltip(node, item, 'cart-item-child-price-info', 'cart-item-child-price-info-tt')
    this.bindQuantityStepper(node, item, 'cart-item-child-participants', 'cart-item-child-participants-label', 'cart-item-child-decrement', 'cart-item-child-increment')
    getElementChild(node, 'cart-item-child-remove')?.addEventListener('click', (): void => {
      void CartService.removeItem(item.itemId)
    })

    return node
  }

  private buildSeriesGroup(items: CartItem[]): HTMLElement | null {
    const node = this.moduleTemplate?.content.firstElementChild?.cloneNode(true) as HTMLElement | undefined
    const list = node === undefined ? null : getElementChild(node, 'cart-item-modules-list')
    const series = items[0]?.series
    if (!node || list === null || series === undefined || series === null) return null

    const total = series.moduleCount ?? items.length
    setText(getElementChild(node, 'cart-item-module-chip'), `${this.formatCount(items.length, total)}\n${this.moduleUnitLabel}`)
    setText(getElementChild(node, 'cart-item-module-series-title'), series.title)

    getElementChild(node, 'cart-item-module-remove')?.addEventListener('click', (): void => {
      void this.removeSeries(items)
    })

    items
      .slice()
      .sort((a: CartItem, b: CartItem): number => (a.series?.modulePosition ?? 0) - (b.series?.modulePosition ?? 0))
      .forEach((item: CartItem): void => {
        const child = this.buildModuleChild(item)
        if (child !== null) list.appendChild(child)
      })

    return node
  }

  private formatCount(count: number, total: number): string {
    return this.moduleCountTemplate.replace('{0}', count.toString()).replace('{1}', total.toString())
  }

  private handleBackdropClick(event: MouseEvent): void {
    if (event.target === this.dialog) {
      this.handleClose()
    }
  }

  private handleChange(event: Event): void {
    const detail = (event as CustomEvent<CartChangeDetail>).detail
    this.render(detail)
  }

  private handleClose(): void {
    this.dialog?.close()
    this.triggerButton?.setAttribute('aria-expanded', 'false')
    this.triggerButton?.focus()
  }

  private async handleExtend(): Promise<void> {
    await CartService.extendReservation()
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.handleClose()
    }
  }

  private handleSubmit(event: SubmitEvent): void {
    event.preventDefault()
    if (this.form === null) return
    const data = new FormData(this.form)
    void data
    this.form.submit()
  }

  private linkTooltip(node: HTMLElement, item: CartItem, triggerHook: string, contentHook: string): void {
    const trigger = getElementChild(node, triggerHook)
    const content = getElementChild(node, contentHook)
    if (trigger === null || content === null) return
    const id = `cart-item-price-info-tt-${item.itemId}`
    content.id = id
    trigger.setAttribute('aria-describedby', id)

    // The booked variant's format wins over the group format when a variant was chosen.
    applyPriceTooltipVariant(content, item.courseVariant?.format ?? item.course.format)
  }

  private async removeSeries(items: CartItem[]): Promise<void> {
    // Sequential removal keeps the cart consistent — concurrent DELETEs on one cart can race.
    for (const item of items) {
      await CartService.removeItem(item.itemId)
    }
  }

  private render(cart: CartChangeDetail): void {
    if (this.itemsContainer === null) return

    this.itemsContainer.replaceChildren()

    const isEmpty = cart.items.length === 0
    this.emptyState?.classList.toggle(IS_HIDDEN, isEmpty === false)
    this.itemsContainer.classList.toggle(IS_HIDDEN, isEmpty)
    this.notice?.classList.toggle(IS_HIDDEN, isEmpty)
    this.form?.classList.toggle(IS_HIDDEN, isEmpty)

    if (this.count !== null) {
      this.count.textContent = `(${cart.items.length.toString()})`
      this.count.classList.toggle(IS_HIDDEN, isEmpty)
    }

    if (isEmpty) {
      this.countdown?.stop()
      return
    }

    const renderedSeries = new Set<string>()
    cart.items.forEach((item: CartItem): void => {
      const seriesId = item.isModule && item.series !== null ? item.series.id : null

      if (seriesId === null) {
        const node = this.buildItem(item)
        if (node !== null) this.itemsContainer?.appendChild(node)
        return
      }

      // Render the group once, at the position of its first module; skip the rest.
      if (renderedSeries.has(seriesId)) return
      renderedSeries.add(seriesId)

      const groupItems = cart.items.filter((candidate: CartItem): boolean => candidate.isModule && candidate.series?.id === seriesId)
      const node = this.buildSeriesGroup(groupItems)
      if (node !== null) this.itemsContainer?.appendChild(node)
    })

    this.countdown?.start(cart.expiresAt)
  }

  private resolveDateText(item: CartItem): string | undefined {
    const courseDate = item.courseDate
    return courseDate?.isDateRange === true
      ? formatDateRange(courseDate.startDate, courseDate.endDate)
      : formatDate(courseDate?.startDate)
  }

  private resolveLocationText(item: CartItem): string | undefined {
    return item.courseDate?.location?.city
      ?? (ONLINE_FORMATS.has(item.course.format) ? this.onlineLocationLabel : undefined)
  }
}

export default CartDialog
